import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AIContentGeneratorProps {
  onContentGenerated: (data: any) => void;
}

export const AIContentGenerator = ({ onContentGenerated }: AIContentGeneratorProps) => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const generateContent = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter keywords or a description for AI generation",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch("/api/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      if (!response.ok) throw new Error("Failed to generate content");

      const data = await response.json();
      onContentGenerated(data);

      toast({
        title: "Success",
        description: "Content generated successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setAiPrompt("");
    }
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="ai" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ai">Generate with AI</TabsTrigger>
          <TabsTrigger value="manual">Write Your Own</TabsTrigger>
        </TabsList>
        <TabsContent value="ai" className="space-y-4">
          <div className="p-4 bg-black/20 rounded-lg space-y-4">
            <Label htmlFor="aiPrompt" className="text-gray-200">AI Content Generator</Label>
            <Textarea
              id="aiPrompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Enter keywords or a brief description of your blog post..."
              className="bg-black/30 border-gray-800 text-white placeholder-gray-500 min-h-[100px]"
            />
            <Button
              type="button"
              onClick={generateContent}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:opacity-90"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate with AI"
              )}
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="manual">
          <div className="text-gray-400 text-sm">
            Fill in the fields below to create your content manually.
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};